import { useCallback, useMemo } from 'react';
import type { UseStepperReturn } from '../types';
import {
    calculateDecrementValue,
    calculateIncrementValue,
    canDecrement as canDecrementUtil,
    canIncrement as canIncrementUtil,
    clampValue,
    normalizeLimits,
} from './utils';

/**
 * Hook personalizado para manejar la lógica del stepper input
 * 
 * Proporciona funcionalidad para incrementar/decrementar valores con límites,
 * validaciones automáticas y estado computado para botones.
 * 
 * @param value - Valor actual del stepper
 * @param onChange - Función para actualizar el valor
 * @param min - Valor mínimo permitido (default: 0)
 * @param max - Valor máximo permitido (default: Infinity)
 * @param step - Paso de incremento/decremento (default: 1)
 * @returns Objeto con estado y funciones para controlar el stepper
 * 
 * @example
 * const { canIncrement, canDecrement, increment, decrement } = useStepper(
 *   value, 
 *   onChange, 
 *   0, 
 *   100, 
 *   1
 * );
 */
export const useStepper = (
    value: number,
    onChange: (value: number) => void,
    min: number = 0,
    max: number = Infinity,
    step: number = 1
): UseStepperReturn => {
    // ============================================================================
    // NORMALIZACIÓN DE LÍMITES
    // ============================================================================

    const limits = useMemo(() =>
        normalizeLimits({ min, max, step }),
        [min, max, step]
    );

    // ============================================================================
    // ESTADO COMPUTADO
    // ============================================================================

    // Valor clampeado dentro de los límites
    const clampedValue = useMemo(() =>
        clampValue(value, limits.min, limits.max),
        [value, limits.min, limits.max]
    );

    // Estado de los botones
    const canIncrement = useMemo(() =>
        canIncrementUtil(clampedValue, limits.max),
        [clampedValue, limits.max]
    );

    const canDecrement = useMemo(() =>
        canDecrementUtil(clampedValue, limits.min),
        [clampedValue, limits.min]
    );

    // ============================================================================
    // FUNCIONES MEMOIZADAS
    // ============================================================================

    /**
     * Incrementa el valor respetando límites y paso
     */
    const increment = useCallback(() => {
        if (canIncrement) {
            const newValue = calculateIncrementValue(
                clampedValue,
                limits.step,
                limits.max
            );
            onChange(newValue);
        }
    }, [canIncrement, clampedValue, limits.step, limits.max, onChange]);

    /**
     * Decrementa el valor respetando límites y paso
     */
    const decrement = useCallback(() => {
        if (canDecrement) {
            const newValue = calculateDecrementValue(
                clampedValue,
                limits.step,
                limits.min
            );
            onChange(newValue);
        }
    }, [canDecrement, clampedValue, limits.step, limits.min, onChange]);

    /**
     * Establece un valor específico con validación
     */
    const setValue = useCallback((newValue: number) => {
        const validValue = clampValue(newValue, limits.min, limits.max);
        onChange(validValue);
    }, [limits.min, limits.max, onChange]);

    // ============================================================================
    // RETORNO DEL HOOK
    // ============================================================================

    return {
        value: clampedValue,
        canIncrement,
        canDecrement,
        increment,
        decrement,
        setValue,
    };
}; 